import React, { useImperativeHandle, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Divider, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';

import RoleChip from 'components/chip/RoleChip';
import ReadOnlyField from 'components/form/ReadOnlyField';
import { MSG_MAX_LENGTH, MSG_MIN_LENGTH } from 'constant/ValidationMessages';
import { Role, Role as RoleConst } from 'stores/AuthenticationStoreAdapter';
import { UserResult, createUserRole, updateUser } from 'stores/UserStoreAdapter';
import { errorDefault, successDefault } from 'utils/Toastify';

export type UserUpdateFormHandle = {
  submit: () => void;
  isEditing: () => boolean;
};

type UserUpdateFormProps = {
  user: UserResult;
  isSelf: boolean;
  editing?: boolean;
  onUserUpdated: (updatedUser: UserResult) => void;
  onClose: () => void;
  onEditingChange: (editing: boolean) => void;
  setProgress: (v: boolean) => void;
};

/** ユーザー管理画面から追加・解除不可のロール */
const UNMANAGEABLE_ROLES: Role[] = ['DEVELOPER'];

const ALL_ROLES: Role[] = Object.keys(RoleConst) as Role[];

/** DetailPanel の p:3 (24px) を打ち消す sticky ヘッダー sx */
const STICKY_HEADER_SX = {
  position: 'sticky',
  top: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
  pb: 1,
  mx: -3,
  px: 3,
  pt: 3,
  mt: -3,
} as const;

/** DetailPanel の p:3 (24px) を打ち消す sticky フッター sx */
const STICKY_FOOTER_SX = {
  position: 'sticky',
  bottom: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderTop: 1,
  borderColor: 'divider',
  py: 2,
  mx: -3,
  px: 3,
} as const;

const UserUpdateForm = React.forwardRef<UserUpdateFormHandle, UserUpdateFormProps>(
  (
    { user, isSelf, editing = false, onUserUpdated, onClose, onEditingChange, setProgress },
    ref,
  ) => {
    const [isEditing, setIsEditing] = useState(editing);

    const [mailAddress, setMailAddress] = useState(user.mailAddress);
    const [userName, setUserName] = useState(user.userName);
    const [password, setPassword] = useState('');

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [copyTooltip, setCopyTooltip] = useState('Copy to clipboard');

    React.useEffect(() => {
      setIsEditing(editing);
    }, [editing]);

    React.useEffect(() => {
      setMailAddress(user.mailAddress);
      setUserName(user.userName);
      setPassword('');
      setErrors({});
      setIsEditing(false);
      onEditingChange(false);
    }, [user]);

    const handleCopyId = () => {
      navigator.clipboard.writeText(user.userId).then(() => {
        setCopyTooltip('Copied!');
        setTimeout(() => setCopyTooltip('Copy to clipboard'), 2000);
      });
    };

    const handleBackToReadOnly = () => {
      setMailAddress(user.mailAddress);
      setUserName(user.userName);
      setPassword('');
      setErrors({});
      setIsEditing(false);
      onEditingChange(false);
    };

    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (mailAddress !== user.mailAddress) {
        if (mailAddress.length < 1) newErrors.mailAddress = MSG_MIN_LENGTH('メールアドレス', 1);
        else if (mailAddress.length > 300)
          newErrors.mailAddress = MSG_MAX_LENGTH('メールアドレス', 300);
      }

      if (userName !== user.userName) {
        if (userName.length < 1) newErrors.userName = MSG_MIN_LENGTH('ユーザー名', 1);
        else if (userName.length > 300) newErrors.userName = MSG_MAX_LENGTH('ユーザー名', 300);
      }

      if (password) {
        if (password.length < 1) newErrors.password = MSG_MIN_LENGTH('パスワード', 1);
        else if (password.length > 300) newErrors.password = MSG_MAX_LENGTH('パスワード', 300);
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
      if (!validate()) return;

      setProgress(true);
      updateUser({
        userId: user.userId,
        ...(mailAddress !== user.mailAddress && { mailAddress }),
        ...(userName !== user.userName && { userName }),
        ...(password && { password }),
      })
        .then(() => {
          successDefault('ユーザー情報を更新しました');
          const updatedUser: UserResult = {
            ...user,
            mailAddress,
            userName,
          };
          onUserUpdated(updatedUser);
          setPassword('');
          setIsEditing(false);
          onEditingChange(false);
        })
        .catch((e: Error) => {
          errorDefault('ユーザー情報の更新に失敗しました', e.message);
        })
        .finally(() => setProgress(false));
    };

    const handleRoleToggle = (role: Role, hasRole: boolean) => {
      const newRoles = hasRole ? user.roles.filter((r) => r !== role) : [...user.roles, role];

      const manageableRoles = newRoles.filter((r) => !UNMANAGEABLE_ROLES.includes(r));
      if (manageableRoles.length === 0) {
        errorDefault('権限は最低1つ必要です');
        return;
      }

      setProgress(true);
      createUserRole({ userId: user.userId, roleIds: newRoles })
        .then(() => {
          successDefault('権限を更新しました');
          const updatedUser: UserResult = {
            ...user,
            roles: newRoles,
          };
          onUserUpdated(updatedUser);
        })
        .catch((e: Error) => {
          errorDefault('権限の更新に失敗しました', e.message);
        })
        .finally(() => setProgress(false));
    };

    useImperativeHandle(ref, () => ({
      submit: handleSave,
      isEditing: () => isEditing,
    }));

    /** 共通ヘッダー */
    const renderHeader = (title: string, subtitle: string, onBack: () => void) => (
      <Box sx={STICKY_HEADER_SX}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={onBack}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              {title}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {user.userId}
            </Typography>
            <Tooltip title={copyTooltip}>
              <IconButton size="small" onClick={handleCopyId}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
          {subtitle}
        </Typography>
      </Box>
    );

    // --- 参照モード ---
    if (!isEditing) {
      return (
        <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {renderHeader(
            isSelf ? 'ユーザー情報' : 'ユーザー詳細',
            isSelf ? 'あなたのアカウント情報です' : 'ユーザー情報を確認します',
            onClose,
          )}

          <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: 2 }}>
            <ReadOnlyField label="ユーザーID" value={user.userId} />
            <ReadOnlyField label="ユーザー名" value={user.userName} />
            <ReadOnlyField label="メールアドレス" value={user.mailAddress} />

            <Divider />

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'action.hover',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                権限{!isSelf && '（クリックで切替）'}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {ALL_ROLES.filter((role) => !UNMANAGEABLE_ROLES.includes(role)).map((role) => {
                  const hasRole = user.roles.includes(role);
                  return (
                    <RoleChip
                      key={role}
                      role={role}
                      variant={hasRole ? 'filled' : 'outlined'}
                      onClick={isSelf ? undefined : () => handleRoleToggle(role, hasRole)}
                    />
                  );
                })}
                {user.roles
                  .filter((role) => UNMANAGEABLE_ROLES.includes(role))
                  .map((role) => (
                    <RoleChip key={role} role={role} variant="filled" />
                  ))}
              </Stack>
            </Box>
          </Stack>

          {/* フッターは外部（Management）で管理 */}
        </Stack>
      );
    }

    // --- 編集モード ---
    return (
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {renderHeader('ユーザー詳細（編集中）', 'ユーザー情報を編集します', handleBackToReadOnly)}

        <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: 2 }}>
          <TextField
            label="ユーザーID"
            value={user.userId}
            disabled
            fullWidth
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          <TextField
            label="ユーザー名"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            fullWidth
            size="small"
            error={!!errors.userName}
            helperText={errors.userName}
            slotProps={{ htmlInput: { maxLength: 300 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          <TextField
            label="メールアドレス"
            value={mailAddress}
            onChange={(e) => setMailAddress(e.target.value)}
            fullWidth
            size="small"
            error={!!errors.mailAddress}
            helperText={errors.mailAddress}
            slotProps={{ htmlInput: { maxLength: 300 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          <TextField
            label="パスワード（変更する場合のみ入力）"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            size="small"
            error={!!errors.password}
            helperText={errors.password}
            slotProps={{ htmlInput: { maxLength: 300 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          <Divider />

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'action.hover',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              権限（クリックで切替）
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {ALL_ROLES.filter((role) => !UNMANAGEABLE_ROLES.includes(role)).map((role) => {
                const hasRole = user.roles.includes(role);
                return (
                  <RoleChip
                    key={role}
                    role={role}
                    variant={hasRole ? 'filled' : 'outlined'}
                    onClick={() => handleRoleToggle(role, hasRole)}
                  />
                );
              })}
              {user.roles
                .filter((role) => UNMANAGEABLE_ROLES.includes(role))
                .map((role) => (
                  <RoleChip key={role} role={role} variant="filled" />
                ))}
            </Stack>
          </Box>
        </Stack>
      </Stack>
    );
  },
);

UserUpdateForm.displayName = 'UserUpdateForm';

export default UserUpdateForm;
